import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../hooks/useAuth';

const Nav = styled.nav<{ $transparent: boolean }>`
  background-color: ${({ theme, $transparent }) =>
    $transparent ? 'transparent' : `${theme.colors.semantic.background}f2`};
  background-image: ${({ theme, $transparent }) =>
    $transparent
      ? `linear-gradient(to bottom, ${theme.colors.semantic.background}b8 0%, transparent 100%)`
      : 'none'};
  backdrop-filter: ${({ $transparent }) => ($transparent ? 'none' : 'blur(18px)')};
  -webkit-backdrop-filter: ${({ $transparent }) => ($transparent ? 'none' : 'blur(18px)')};
  border-bottom: ${({ theme, $transparent }) =>
    $transparent ? 'none' : `1px solid ${theme.colors.semantic.border}`};
  position: fixed;
  inset: 0 0 auto;
  /* Keep the public shell beneath existing modal/viewer overlays. */
  z-index: 999;
  padding-top: env(safe-area-inset-top, 0px);
  transition: background-color ${({ theme }) => theme.transitions.fast},
    border-color ${({ theme }) => theme.transitions.fast};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const NavContainer = styled.div`
  max-width: ${({ theme }) => theme.layout.contentMaxWidth};
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing.md};
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: var(--site-header-height);

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 0 ${({ theme }) => theme.spacing.lg};
  }
`;

const Logo = styled(Link)`
  color: ${({ theme }) => theme.colors.semantic.textPrimary};
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  letter-spacing: 0.08em;
  text-decoration: none;
  text-transform: uppercase;
  white-space: nowrap;

  &:hover {
    color: ${({ theme }) => theme.colors.semantic.accentHover};
  }
`;

const DesktopNavLinks = styled.div`
  display: none;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    display: flex;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    gap: ${({ theme }) => theme.spacing.lg};
  }
`;

const NavLink = styled(Link)<{ $isActive: boolean; $isMobile?: boolean }>`
  display: ${({ $isMobile }) => ($isMobile ? 'flex' : 'inline-flex')};
  align-items: center;
  min-height: ${({ $isMobile }) => ($isMobile ? '48px' : '44px')};
  padding: ${({ theme, $isMobile }) =>
    $isMobile ? `${theme.spacing.sm} ${theme.spacing.md}` : `${theme.spacing.sm} ${theme.spacing.xs}`};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border-left: ${({ $isMobile, theme }) =>
    $isMobile ? `3px solid ${theme.colors.semantic.border}` : '0'};
  color: ${({ theme, $isActive }) =>
    $isActive ? theme.colors.semantic.textPrimary : theme.colors.semantic.textSecondary};
  font-size: ${({ $isMobile }) => ($isMobile ? '1rem' : '0.875rem')};
  font-weight: ${({ theme, $isActive }) =>
    $isActive ? theme.typography.fontWeight.semibold : theme.typography.fontWeight.medium};
  letter-spacing: 0.04em;
  text-decoration: none;
  text-transform: uppercase;
  transition: color ${({ theme }) => theme.transitions.fast},
    background-color ${({ theme }) => theme.transitions.fast},
    border-color ${({ theme }) => theme.transitions.fast};

  ${({ $isMobile, $isActive, theme }) => $isMobile && $isActive && `
    border-left-color: ${theme.colors.semantic.focus};
    background-color: ${theme.colors.semantic.surfaceRaised};
  `}

  &:hover {
    color: ${({ theme }) => theme.colors.semantic.textPrimary};
    background-color: ${({ theme }) => theme.colors.semantic.surfaceRaised};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const MobileMenuButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  padding: 10px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.semantic.textPrimary};

  &:hover {
    background-color: ${({ theme }) => theme.colors.semantic.surfaceRaised};
  }

  span {
    width: 22px;
    height: 2px;
    margin: 3px 0;
    background-color: currentColor;
    border-radius: 2px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    display: none;
  }
`;

const DrawerBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1004;
  background: rgba(17, 17, 15, 0.76);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
`;

const DrawerContainer = styled.div`
  position: fixed;
  inset: 0 0 0 auto;
  z-index: 1005;
  display: flex;
  flex-direction: column;
  width: min(320px, calc(100vw - 24px));
  height: 100vh;
  height: 100dvh;
  padding-top: calc(env(safe-area-inset-top, 0px) + 8px);
  padding-bottom: env(safe-area-inset-bottom, 0px);
  overflow: hidden;
  background: ${({ theme }) => theme.colors.semantic.surface};
  border-left: 1px solid ${({ theme }) => theme.colors.semantic.borderStrong};
  box-shadow: -12px 0 36px rgba(0, 0, 0, 0.42);

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    display: none;
  }
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 0 0 auto;
  min-height: 64px;
  padding: 0 ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.semantic.border};
`;

const DrawerLogo = styled(Link)`
  color: ${({ theme }) => theme.colors.semantic.textPrimary};
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  letter-spacing: 0.06em;
  text-decoration: none;
  text-transform: uppercase;
`;

const DrawerCloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 44px;
  width: 44px;
  height: 44px;
  border: 1px solid ${({ theme }) => theme.colors.semantic.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.semantic.textPrimary};
  font-size: 1.75rem;
  line-height: 1;

  &:hover {
    background: ${({ theme }) => theme.colors.semantic.surfaceRaised};
  }
`;

const MobileNavLinks = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.spacing.md};
  overflow-y: auto;
  overscroll-behavior: contain;
`;

const DrawerFooter = styled.div`
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.md};
  border-top: 1px solid ${({ theme }) => theme.colors.semantic.border};
`;

const AdminLink = styled(Link)<{ $isMobile?: boolean }>`
  display: ${({ $isMobile }) => ($isMobile ? 'flex' : 'inline-flex')};
  align-items: center;
  justify-content: center;
  min-height: ${({ $isMobile }) => ($isMobile ? '48px' : '44px')};
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
  border: 1px solid ${({ theme }) => theme.colors.semantic.borderStrong};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.semantic.textPrimary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  text-decoration: none;

  &:hover {
    background: ${({ theme }) => theme.colors.semantic.surfaceRaised};
  }
`;

const AdminButton = styled.button<{ $isMobile?: boolean }>`
  display: ${({ $isMobile }) => ($isMobile ? 'flex' : 'inline-flex')};
  align-items: center;
  justify-content: center;
  min-height: ${({ $isMobile }) => ($isMobile ? '48px' : '44px')};
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
  border: 1px solid ${({ theme }) => theme.colors.semantic.borderStrong};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: transparent;
  color: ${({ theme }) => theme.colors.semantic.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};

  &:hover {
    background: ${({ theme }) => theme.colors.semantic.surfaceRaised};
    color: ${({ theme }) => theme.colors.semantic.textPrimary};
  }
`;

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function isVisible(element: HTMLElement) {
  const styles = window.getComputedStyle(element);
  return styles.display !== 'none' && styles.visibility !== 'hidden' && !element.hasAttribute('aria-hidden');
}

export function Navigation() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAtTop, setIsAtTop] = useState(() => window.scrollY < 10);
  const location = useLocation();
  const { isAuthenticated, user, logout } = useAuth();
  const menuTriggerRef = useRef<HTMLButtonElement>(null);
  const logoRef = useRef<HTMLAnchorElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previousPathRef = useRef(location.pathname);
  const wasMenuOpenRef = useRef(false);

  const isHomePage = location.pathname === '/';
  const isTransparent = isHomePage && isAtTop;

  useEffect(() => {
    if (!isHomePage) return undefined;

    const handleScroll = () => setIsAtTop(window.scrollY < 10);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHomePage]);

  const navItems = [
    { path: '/gallery', label: 'Gallery' },
    { path: '/artists', label: 'Artists' },
    { path: '/exhibitions', label: 'Collections' },
    { path: '/about', label: 'About the Studio' },
  ];

  const isActiveRoute = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  const restoreFocus = useCallback(() => {
    const candidates: Array<HTMLElement | null> = [menuTriggerRef.current, logoRef.current];
    const visibleTarget = candidates.find(
      (candidate): candidate is HTMLElement => Boolean(candidate && document.contains(candidate) && isVisible(candidate)),
    );

    visibleTarget?.focus();
  }, []);

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  const handleLogout = async () => {
    await logout();
    closeMenu();
  };

  useEffect(() => {
    if (previousPathRef.current === location.pathname) return;
    previousPathRef.current = location.pathname;
    if (isMenuOpen) closeMenu();
  }, [closeMenu, isMenuOpen, location.pathname]);

  // Lock only the inline style that this drawer owns, then restore any prior
  // page state. This keeps nested consumers and scrolled positions intact.
  useEffect(() => {
    if (!isMenuOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    const previousPaddingRight = document.body.style.paddingRight;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('drawer-open');

    const handleResize = () => {
      if (window.innerWidth >= 1024) closeMenu();
    };
    const handleFocusIn = (event: FocusEvent) => {
      if (drawerRef.current && !drawerRef.current.contains(event.target as Node)) {
        closeButtonRef.current?.focus();
      }
    };
    window.addEventListener('resize', handleResize);
    document.addEventListener('focusin', handleFocusIn, true);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPaddingRight;
      document.body.classList.remove('drawer-open');
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('focusin', handleFocusIn, true);
    };
  }, [closeMenu, isMenuOpen]);

  useLayoutEffect(() => {
    if (isMenuOpen) {
      wasMenuOpenRef.current = true;
      return;
    }

    if (wasMenuOpenRef.current) {
      wasMenuOpenRef.current = false;
      restoreFocus();
    }
  }, [isMenuOpen, restoreFocus]);

  useEffect(() => {
    if (!isMenuOpen) return undefined;
    const timeout = window.setTimeout(() => closeButtonRef.current?.focus(), 0);
    return () => window.clearTimeout(timeout);
  }, [isMenuOpen]);

  const handleDrawerKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeMenu();
      return;
    }

    if (event.key !== 'Tab' || !drawerRef.current) return;
    const focusable = Array.from(drawerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(isVisible);
    if (focusable.length === 0) {
      event.preventDefault();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <>
      <Nav aria-label="Main navigation" $transparent={isTransparent}>
        <NavContainer>
          <Logo ref={logoRef} to="/">Joanne Art Class</Logo>

          <DesktopNavLinks>
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                $isActive={isActiveRoute(item.path)}
                aria-current={isActiveRoute(item.path) ? 'page' : undefined}
              >
                {item.label}
              </NavLink>
            ))}

            {isAuthenticated ? (
              <>
                <AdminLink to="/admin">Dashboard</AdminLink>
                <AdminButton
                  onClick={handleLogout}
                  aria-label={user?.email ? `Log out ${user.email}` : 'Log out'}
                  title={user?.email ? `Log out ${user.email}` : 'Log out'}
                >
                  Logout
                </AdminButton>
              </>
            ) : (
              <AdminLink to="/admin/login">Admin Login</AdminLink>
            )}
          </DesktopNavLinks>

          <MobileMenuButton
            ref={menuTriggerRef}
            type="button"
            onClick={() => setIsMenuOpen((open) => !open)}
            aria-label={isMenuOpen ? 'Close mobile menu' : 'Toggle mobile menu'}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-navigation-drawer"
          >
            <span />
            <span />
            <span />
          </MobileMenuButton>
        </NavContainer>
      </Nav>

      {isMenuOpen && (
        <>
          <DrawerBackdrop aria-hidden="true" onClick={closeMenu} />
          <DrawerContainer
            ref={drawerRef}
            id="mobile-navigation-drawer"
            role="dialog"
            aria-modal="true"
            aria-label="Mobile navigation"
            data-drawer="true"
            onKeyDown={handleDrawerKeyDown}
          >
            <DrawerHeader>
              <DrawerLogo to="/" onClick={closeMenu}>Joanne Art Class</DrawerLogo>
              <DrawerCloseButton ref={closeButtonRef} type="button" onClick={closeMenu} aria-label="Close menu">
                <span aria-hidden="true">×</span>
              </DrawerCloseButton>
            </DrawerHeader>

            <MobileNavLinks>
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  $isActive={isActiveRoute(item.path)}
                  $isMobile
                  aria-current={isActiveRoute(item.path) ? 'page' : undefined}
                  onClick={closeMenu}
                >
                  {item.label}
                </NavLink>
              ))}
            </MobileNavLinks>

            <DrawerFooter>
              {isAuthenticated ? (
                <>
                  <AdminLink to="/admin" $isMobile onClick={closeMenu}>Dashboard</AdminLink>
                  <AdminButton
                    $isMobile
                    onClick={handleLogout}
                    aria-label={user?.email ? `Log out ${user.email}` : 'Log out'}
                    title={user?.email ? `Log out ${user.email}` : 'Log out'}
                  >
                    Logout
                  </AdminButton>
                </>
              ) : (
                <AdminLink to="/admin/login" $isMobile onClick={closeMenu}>Admin Login</AdminLink>
              )}
            </DrawerFooter>
          </DrawerContainer>
        </>
      )}
    </>
  );
}
